import React from 'react';
import PropTypes from 'prop-types';
import DropDownButton from '../buttons/DropDownButton';
import SplitButtonCascade from '../buttons/SplitButtonCascade';
import LoadingPanel from '../LoadingPanel';
import Dialog from '../meta/Dialog';
import flags from '../utils/flags';
import { buildUrl, getPrimaryField } from '../utils/helper';
import { addOffset } from '../utils/convert';
import ConfirmDialog from '../dialogs/ConfirmDialog';

const _gridPrintItems = [
  { text: "Tlač mriežky do XLSX", imageUrl: buildUrl('assets/icons/toolbar_cg_xlsx.svg') },
  { text: "Tlač mriežky do PDF", imageUrl: buildUrl('assets/icons/toolbar_cg_pdf.svg') }
];

/**
 * Print component
 */
class PrintComponent extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      tl: null,
      isOpenPred: false,
      isOpenConfirm: false,
      titlePred: "",
      nIdentifPred: "",
      isPrintableToDb: false
    }
  }

  handleTlac = async (index, item) => {
    this.setState({ isLoading: true });
    if (item?.iTl > 0) {
      if (item?.iFrmPred) {
        this.setState({ isOpenPred: true, tl: item, titlePred: item?.text, nIdentifPred: item?.nIdentifPred, isPrintableToDb: this.isPrintableToDb(item?.iSposList) });
      } else {
        if (this.isPrintableToDb(item?.iSposList)) {
          this.setState({ isOpenConfirm: true, tl: item });
        }
        else await this.props.onTlac(item);
      }
    } else {
      this.props.onPrint(index, item.text);
    }
    this.setState({ isLoading: false });
  }

  handleClosePred = () => {
    this.setState({ isOpenPred: false, tl: null, titlePred: "", nIdentifPred: "", isPrintableToDb: false });
  }

  handleCloseConfirm = () => {
    this.setState({ isOpenConfirm: false, tl: null, isPrintableToDb: false });
  }

  processTlacData = (state) => {
    const detailData = state[state.primaryField]?.detailData;
    let tlac = {
      ...this.state.tl,
      params: this.state.tl?.params ?? {},
      dialogState: { ...state }
    }
    for (const [key, value] of Object.entries(detailData)) {
      const field = tlac.dialogState.detailSchema.find(x => x.field === key);
      const primaryField = getPrimaryField(field?.primaryField);
      if (value?.hasOwnProperty(primaryField)) {
        tlac.params[key + (primaryField === "I_HOD" ? "" : "." + primaryField)] = value[primaryField];
      } else if (value?.hasOwnProperty("start") && value?.hasOwnProperty("end")) {
        tlac.params[key + '.start'] = addOffset(value?.start).toISOString();
        tlac.params[key + '.end'] = addOffset(value?.end).toISOString();
      } else {
        tlac.params[key] = (value instanceof Date ? addOffset(value).toISOString() : value);
      }
      if (Array.isArray(value)) {
        let array = value.map(element => {
          if (element.hasOwnProperty(primaryField)) {
            return element[primaryField]
          } else return element
        });
        tlac.params[key] = array.join(',');
      }
    }
    return tlac;
  }

  handleConfirmPred = async (state) => {
    const tlac = this.processTlacData(state);
    this.setState({ isLoading: true });
    await this.props.onTlac(tlac);
    this.setState({ isLoading: false });
  }

  handlePrint = async () => {
    this.setState({ isLoading: true });
    this.handleCloseConfirm();
    await this.props.onTlac(this.state.tl);
    this.setState({ isLoading: false });
  }

  handleDatabasePrint = async () => {
    this.setState({ isLoading: true });
    this.handleCloseConfirm();
    await this.props.onTlac(this.state.tl, "database");
    this.setState({ isLoading: false });
  }

  handlePredDatabasePrint = async (state) => {
    this.setState({ isLoading: true });
    const tlac = this.processTlacData(state);
    await this.props.onTlac(tlac, "database");
    this.setState({ isLoading: false });
  }

  handlePreview = async (state) => {
    this.setState({ isLoading: true });
    const tlac = this.processTlacData(state);
    await this.props.onTlac(tlac);
    this.setState({ isLoading: false });
  }

  getIcon = (extension) => {
    switch (extension) {
      case ".multi":
        return "toolbar_multiformat";
      case ".chart":
        return "toolbar_chart";
      case ".pdf":
        return "toolbar_cg_pdf";
      case ".doc":
      case ".docx":
        return "toolbar-ikony_ms-word";
      case ".xls":
      case ".xlsx":
        return "toolbar_cg_xlsx";
      case ".xml":
        return "toolbar_xml_file";
      default:
        return "toolbar_tlac_zostavy";
    }
  }

  isDisabled = (iSposList) => {
    return (!this.props.isMultiSelected && !iSposList?.includes(2) && !iSposList?.includes(4)) ||
      (this.props.isSelected && !iSposList?.includes(1) && !iSposList?.includes(3) && !iSposList?.includes(4)) ||
      (this.props.isMultiSelected && !this.props.isSelected && !iSposList?.includes(3) && !iSposList?.includes(4));
  }

  isPrintableToDb = (iSposList) => {
    return iSposList?.includes(5);
  }

  getTlacKatItem = (tlacKat) => {
    return {
      text: tlacKat.text,
      disableClick: true,
      render: SplitButtonCascade,
      direction: "left",
      onItemClick: (index, item) => this.handleTlac(index, item),
      items: tlacKat.items.map(x => (
        {
          ...x,
          disabled: x.disabled || this.isDisabled(x.iSposList),
          imageUrl: buildUrl(`assets/icons/${this.getIcon(x.extension)}.svg`)
        }
      ))
    };
  }

  getTlacItem = (tlac) => {
    return {
      ...tlac,
      disabled: tlac.disabled || this.isDisabled(tlac.iSposList),
      imageUrl: buildUrl(`assets/icons/${this.getIcon(tlac.extension)}.svg`),
      onItemClick: (index, item) => this.handleTlac(index, item)
    };
  }

  getPrintItems = () => {
    if (this.props.printItems?.length > 0) {
      return [..._gridPrintItems, ...this.props.printItems];
    }
    let printItems = this.props.gridPrintItemsHidden ? [] : [{
      text: "Tlač mriežky",
      disableClick: true,
      render: SplitButtonCascade,
      direction: "left",
      onItemClick: (index, item) => this.handleTlac(index, item),
      items: _gridPrintItems
    }];
    if (this.props?.tlac?.tlacKatList?.length > 0) {
      printItems = [
        ...printItems,
        ...this.props.tlac.tlacKatList.map(this.getTlacKatItem)
      ]
    }
    if (this.props?.tlac?.items?.length > 0) {
      printItems = [
        ...printItems,
        ...this.props.tlac.items.map(this.getTlacItem)
      ]
    }
    return printItems;
  }

  handlePrintReducer = async (action, data, state, frmState) => {
    let _state = { ...state, selected: this.props.selected };
    return this.props.reducer(action, data, _state, frmState);
  }

  render() {
    return (
      <>
        <DropDownButton
          text=""
          popupSettings={{ popupClass: 'dropdown-print' }}
          icon="print"
          title="Tlač"
          items={this.getPrintItems()}
          onClick={this.props.onPrint}
          disabled={Boolean(this.props.disabled & (flags.PRINT | flags.TOOLBAR))}
          imageUrl={buildUrl('assets/icons/toolbar_cg_print.svg')}
        />
        {this.state.isOpenPred && (
          this.state.isPrintableToDb ? (<Dialog
            primaryField={this.state.nIdentifPred}
            className={this.state.nIdentifPred}
            isOpen={this.state.isOpenPred}
            onClose={this.handleClosePred}
            onConfirm={this.handlePredDatabasePrint}
            onMiddleClick={this.handlePreview}
            middleText="Náhľad"
            title={this.state.titlePred}
            reducer={this.handlePrintReducer}
            componentFactory={this.props.componentFactory}
            confirmText="Uložiť do databázy"
          />)
            :
            (<Dialog
              primaryField={this.state.nIdentifPred}
              className={this.state.nIdentifPred}
              isOpen={this.state.isOpenPred}
              onClose={this.handleClosePred}
              onConfirm={this.handleConfirmPred}
              title={this.state.titlePred}
              reducer={this.handlePrintReducer}
              componentFactory={this.props.componentFactory}
              confirmText="Vytlačiť"
            />)
        )}
        {this.state.isOpenConfirm && <ConfirmDialog
          text={`Vyberte požadovanú formu tlače pre tlažovú zostavu: ${this.state.tl.text}`}
          onClose={this.handleCloseConfirm}
          onCancel={this.handleDatabasePrint}
          onConfirm={this.handlePrint}
          isOpen={this.state.isOpenConfirm}
          confirmText="Vytlačiť"
          cancelText="Uložiť do databázy"
        />
        }
        <LoadingPanel isLoading={this.state.isLoading} />
      </>
    );
  }
}

PrintComponent.propTypes = {
  disabled: PropTypes.bool,
  printItems: PropTypes.array,
  onPrint: PropTypes.func,
  tlac: PropTypes.object,
  onTlac: PropTypes.func,
  isSelected: PropTypes.bool,
  isMultiSelected: PropTypes.bool,
  reducer: PropTypes.func,
  componentFactory: PropTypes.func,
  gridPrintItemsHidden: PropTypes.bool,
}

export default PrintComponent;